/**
 * useAiAnalysisDraftSessionPool：弹窗预览会话 + "Apply 接管"会话池。
 *
 * 核心目标：
 * - 弹窗预览（preview）需要一条可反复重跑/丢弃的 SSE 会话；
 * - Apply-and-Exit 的语义是"卡片接管弹窗预览的同一条会话"，而不是再发起一次新的分析请求；
 * - 因此需要维护一个"可切换归属"的 session key（A/B），避免同一会话被两个视图同时接管。
 *
 * 关键约束：
 * - attachedSessionKey 与 previewSessionKey 可能相同（发生 Apply 后），此时禁止 discardPreviewSession 清空该会话；
 * - 取消 SSE 时需要同时调用后端 cancel 接口 + 本地 cancelStreaming/clearConversations，避免后台继续生成占用资源；
 * - SSE payload 的 artifact.parts 可能以"整段重发"的形式更新，本地需要做增量（delta）拼接来避免重复渲染。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLocale } from 'umi';
import { v4 as uuidv4 } from 'uuid';

import type {
  IRawStreamChunk,
  IUseStreamingConversationResult,
  TStreamEvent,
} from '@dbl/core/global';
import { useStreamingConversation } from '@dbl/core/global';
import { buildTokenAwareRequest } from '@dbl/services/common';

import { buildAiAnalysisStreamingInput, cancelAiAnalysis, type IBuildAiAnalysisInputParams } from '../runAiAnalysis';
import type { IAiAnalysisStreamingInput } from '../types';

type TSessionKey = 'A' | 'B';

interface IAiAnalysisDraftInternalInput extends IAiAnalysisStreamingInput {
  __sessionKey: TSessionKey;
  __requestSeq: number;
}

interface IAiAnalysisSessionLedgerItem {
  sessionId: string;
  messageId?: string;
  requestSeq: number;
}

export interface IUseAiAnalysisDraftSessionPoolParams {
  gameCode: string;
  dashboardId: string;
  queries?: {
    filters?: unknown[] | null;
    user_filter?: unknown;
    [key: string]: unknown;
  };
  formatMessage: (descriptor: { id: string }) => string;
}

export interface IUseAiAnalysisDraftSessionPoolResult {
  attachedCardId?: string;
  attachedSessionId: string;
  attachedSession: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  previewSession: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;

  preparePreviewSession: () => void;
  runPreviewAnalysis: (params: IBuildAiAnalysisInputParams) => Promise<void>;
  runAttachedAnalysisByCardId: (cardId: string, params: IBuildAiAnalysisInputParams) => Promise<void>;
  detachPreviewToCard: (cardId: string) => void;
  discardPreviewSession: () => void;

  cleanupOnDeleteCard: (cardId: string) => void;
  /** 释放卡片对 preview 会话的接管（不删卡片），用于布局图表变更后回到待确认态 */
  releaseAttachedSessionByCardId: (cardId: string) => void;
  isCardAttached: (cardId: string) => boolean;
  stopAttachedSessionByCardId: (cardId: string) => void;

  /** 设置 AI 分析耗时指标回调，在 preview session 收到 __metrics__ 帧时触发。传 null 清除。 */
  setAiMetricsCallback: (cb: ((durationMs: number) => void) | null) => void;
}

const getOppositeKey = (key: TSessionKey): TSessionKey => (key === 'A' ? 'B' : 'A');

export const useAiAnalysisDraftSessionPool = (
  params: IUseAiAnalysisDraftSessionPoolParams,
): IUseAiAnalysisDraftSessionPoolResult => {
  const { gameCode, dashboardId, queries, formatMessage } = params;
  // ===== 非渲染态缓存：这些值属于"会话编排上下文"，不应该驱动界面重渲染 =====
  // 用 messageId 维度缓存 artifact.parts，支持"整段重发"场景下计算 delta。
  const artifactTextPartsByMessageIdRef = useRef<Record<string, string[]>>({});

  // AI 分析耗时指标回调：在 SSE 流返回 __metrics__ 帧时触发，由 controller 设置。
  const aiMetricsCallbackRef = useRef<((durationMs: number) => void) | null>(null);

  // ===== Session Ledger：会话定位信息的权威真值源 =====
  // - sessionId：后端要求每轮都新
  // - messageId：协议层生成，onBeforeSend 回填
  // - requestSeq：前端序列号，用于丢弃旧请求晚到 chunk
  const ledgerRef = useRef<Record<TSessionKey, IAiAnalysisSessionLedgerItem>>({
    A: { sessionId: uuidv4(), requestSeq: 0 },
    B: { sessionId: uuidv4(), requestSeq: 0 },
  });

  // 仅用于对外暴露 attachedSessionId（卡片侧 run 入口依赖该值透传 session_id）。
  // 取消/清理的定位信息均以 ledgerRef 为准，不依赖该 state。
  const [sessionIdByKey, setSessionIdByKey] = useState<Record<TSessionKey, string>>({
    A: ledgerRef.current.A.sessionId,
    B: ledgerRef.current.B.sessionId,
  });

  // sessionConfig 是传给 useStreamingConversation 的统一协议层配置。
  // 它把业务参数（gameCode/dashboardId/queries）与 SSE 协议细节（messageId、parseChunk）封装在一起。
  const sessionConfig = useMemo(() => ({
    buildRequest: async (
      input: IAiAnalysisDraftInternalInput,
      context: { messageId: string },
    ) => (
      // 说明：input 中包含内部字段（__sessionKey/__requestSeq），必须避免直接透传给后端。
      buildTokenAwareRequest({
        url: '/api/v2/datalab/ai/chat',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        data: {
          game_code: gameCode,
          dashboard_id: dashboardId,
          queries,
          ...(() => {
            const { __sessionKey: _sessionKey, __requestSeq: _requestSeq, ...rest } = input;
            return rest;
          })(),
          language: getLocale(),
          session_id: input.session_id,
          message_id: context.messageId,
          message: '',
          // 后端计算动态日期时间需要的
          request_time: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
    ),
    createMessageId: () => uuidv4(),
    onBeforeSend: ({ messageId, input }: { messageId: string; input: IAiAnalysisDraftInternalInput }) => {
      // 显式 sessionKey：避免通过 session_id 反查归属导致隐式耦合。
      const { __sessionKey: sessionKey } = input;
      ledgerRef.current[sessionKey].messageId = messageId;
    },
    parseChunk: (rawChunk: IRawStreamChunk, context: { messageId: string; input: IAiAnalysisDraftInternalInput }): TStreamEvent[] => {
      // 空帧直接忽略，避免对下游状态机产生无意义扰动。
      if (!rawChunk?.data) {
        return [];
      }

      // 防晚到 chunk：只承认 sessionKey 下最新一轮请求。
      const { __sessionKey: sessionKey, __requestSeq: requestSeq } = context.input;
      if (requestSeq !== ledgerRef.current[sessionKey].requestSeq) {
        return [];
      }
      try {
        const payload = JSON.parse(rawChunk.data);

        if (
          rawChunk.event === 'done'
          || payload?.type === 'done'
          || payload?.result?.final === true
          || payload?.result?.status?.state === 'completed'
          || payload?.id === 'completed'
        ) {
          return [{ kind: 'DONE' }];
        }
        if (rawChunk.event === 'message' && payload?.type === 'text') {
          return [{ kind: 'TEXT_DELTA', text: String(payload?.content || '') }];
        }
        if (rawChunk.event === 'message' && payload?.type === 'progress') {
          return [{ kind: 'TEXT_DELTA', text: `${String(payload?.content || '')}\n` }];
        }
        const artifactParts = payload?.result?.artifact?.parts;
        if (Array.isArray(artifactParts)) {
          // 后端有时不会直接给 content delta，而是回传"当前完整 artifact parts"。
          // 这里把它转成 useStreamingConversation 能消费的 TEXT_DELTA 事件流。
          const currentTextParts = artifactParts
            .filter((part) => part?.type === 'text' && typeof part?.text === 'string')
            .map((part) => part.text);
          if (currentTextParts.length) {
            const messageId = context.messageId;
            const prevTextParts = artifactTextPartsByMessageIdRef.current[messageId] || [];
            const events: TStreamEvent[] = [];
            const maxPrefixLength = Math.min(prevTextParts.length, currentTextParts.length);
            let commonPrefixCount = 0;
            // 先找出"前缀完全相同"的 parts 数量。
            // 这样后续就只需要处理新增部分，避免把历史内容重复追加到 answer 中。
            while (
              commonPrefixCount < maxPrefixLength
              && prevTextParts[commonPrefixCount] === currentTextParts[commonPrefixCount]
            ) {
              commonPrefixCount += 1;
            }

            // 处理"同一个 part 从短变长"的情况：以 commonPrefixCount 对齐后，按字符串前缀计算 delta。
            if (
              commonPrefixCount < prevTextParts.length
              && commonPrefixCount < currentTextParts.length
            ) {
              const prevPart = prevTextParts[commonPrefixCount] || '';
              const currentPart = currentTextParts[commonPrefixCount] || '';
              if (currentPart.startsWith(prevPart)) {
                const partDelta = currentPart.slice(prevPart.length);
                if (partDelta) {
                  events.push({
                    kind: 'TEXT_DELTA',
                    text: partDelta,
                  });
                }
                commonPrefixCount += 1;
              }
            }

            // 追加剩余 parts（后端可能一次性带回多段）。
            for (let i = commonPrefixCount; i < currentTextParts.length; i += 1) {
              const text = currentTextParts[i];
              if (!text) {
                continue;
              }
              events.push({
                kind: 'TEXT_DELTA',
                text,
              });
            }

            artifactTextPartsByMessageIdRef.current[messageId] = currentTextParts;
            return events;
          }
        }

        // __metrics__ 恒在 SSE 流末尾出现，放最后判断避免 99.9% 的 chunk 做无意义比较。
        if (payload?.id === '__metrics__' && typeof payload?.result?.duration_ms === 'number') {
          aiMetricsCallbackRef.current?.(payload.result.duration_ms);
          aiMetricsCallbackRef.current = null;
          return [];
        }

        return [];
      } catch (error) {
        const chunkData = String(rawChunk?.data || '').trim();
        // 后端 SSE 可能包含非 JSON 控制帧（如 [DONE]），这些帧应忽略而不是打断渲染。
        if (!chunkData || chunkData === '[DONE]') {
          return [];
        }
        return [
          {
            kind: 'ERROR',
            error,
            message: formatMessage({ id: 'dbl_ai_analysis_stream_parse_error' }),
          },
        ];
      }
    },
  }), [dashboardId, formatMessage, gameCode, queries]);

  // 这里固定初始化两条 conversation session：
  // - 一条给 attached（被卡片接管）；
  // - 一条给 preview（弹窗内预览）。
  // 不在需要时动态创建，是为了让"切换归属"只变更 key，不变更 session 实例。
  const sessionA = useStreamingConversation<IAiAnalysisDraftInternalInput>(sessionConfig);
  const sessionB = useStreamingConversation<IAiAnalysisDraftInternalInput>(sessionConfig);

  // ===== 会话归属状态 =====
  // attachedCardId: 当前哪张卡片接管了 preview 会话；为空表示没有卡片处于"接管态"。
  const [attachedCardId, setAttachedCardId] = useState<string | undefined>(undefined);
  // attachedSessionKey: 当前被卡片展示的那条 session 是 A 还是 B。
  const [attachedSessionKey, setAttachedSessionKey] = useState<TSessionKey>('A');
  // previewSessionKey: 弹窗预览当前使用哪条 session。
  // 初始与 attached 错开，方便一开始就把"预览"和"卡片展示"放在不同轨道上。
  const [previewSessionKey, setPreviewSessionKey] = useState<TSessionKey>('B');

  // 根据 key 取真实 session 实例。
  // 单独抽成回调，便于 attachedSession / previewSession 复用同一套映射逻辑。
  const getSessionByKey = useCallback((key: TSessionKey) => (key === 'A' ? sessionA : sessionB), [sessionA, sessionB]);

  // 这两个 useMemo 体现的是"逻辑角色 -> 实际 session 实例"的映射。
  // 角色会变（key 切换），但 sessionA/sessionB 实例本身不会频繁重建。
  const attachedSession = useMemo(
    () => getSessionByKey(attachedSessionKey),
    [attachedSessionKey, getSessionByKey],
  );
  const previewSession = useMemo(
    () => getSessionByKey(previewSessionKey),
    [previewSessionKey, getSessionByKey],
  );

  const { cancelStreaming: cancelStreamingA, clearConversations: clearConversationsA } = sessionA;
  const { cancelStreaming: cancelStreamingB, clearConversations: clearConversationsB } = sessionB;

  // 组件卸载时兜底清空两条 session。
  // 完整方案下：若卸载时仍在 streaming，仍做一次 best-effort 的后端 cancel，避免无主生成占用资源。
  useEffect(() => () => {
    const isStreamingA = sessionA.streamingState === 'pending' || sessionA.streamingState === 'progressing';
    if (isStreamingA) {
      const latestConversation = sessionA.conversations[sessionA.conversations.length - 1];
      const messageId = latestConversation?.messageId || ledgerRef.current.A.messageId;
      cancelAiAnalysis({
        sessionId: ledgerRef.current.A.sessionId,
        messageId,
      }).catch(() => {});
      clearArtifactCacheByMessageId(messageId);
    }

    const isStreamingB = sessionB.streamingState === 'pending' || sessionB.streamingState === 'progressing';
    if (isStreamingB) {
      const latestConversation = sessionB.conversations[sessionB.conversations.length - 1];
      const messageId = latestConversation?.messageId || ledgerRef.current.B.messageId;
      cancelAiAnalysis({
        sessionId: ledgerRef.current.B.sessionId,
        messageId,
      }).catch(() => {});
      clearArtifactCacheByMessageId(messageId);
    }

    cancelStreamingA();
    clearConversationsA();
    cancelStreamingB();
    clearConversationsB();
  }, [cancelStreamingA, clearConversationsA, cancelStreamingB, clearConversationsB]);

  const clearArtifactCacheByMessageId = useCallback((messageId?: string) => {
    if (!messageId) {
      return;
    }
    if (!artifactTextPartsByMessageIdRef.current[messageId]) {
      return;
    }
    const next = { ...artifactTextPartsByMessageIdRef.current };
    delete next[messageId];
    artifactTextPartsByMessageIdRef.current = next;
  }, []);

  const cancelLatestByKey = useCallback((sessionKey: TSessionKey) => {
    const targetSession = getSessionByKey(sessionKey);
    const isStreaming = targetSession.streamingState === 'pending' || targetSession.streamingState === 'progressing';
    if (isStreaming) {
      const latestConversation = targetSession.conversations[targetSession.conversations.length - 1];
      const messageId = latestConversation?.messageId || ledgerRef.current[sessionKey].messageId;
      cancelAiAnalysis({
        sessionId: ledgerRef.current[sessionKey].sessionId,
        messageId,
      }).catch(() => {});
      clearArtifactCacheByMessageId(messageId);
    }
    targetSession.cancelStreaming();
    targetSession.clearConversations();
    // 清除埋点回调，防止停止后回调残留。
    aiMetricsCallbackRef.current = null;
  }, [clearArtifactCacheByMessageId, getSessionByKey]);

  const startAnalysisByKey = useCallback(async (sessionKey: TSessionKey, runParams: IBuildAiAnalysisInputParams) => {
    const session = getSessionByKey(sessionKey);
    // 先本地 stop，保证不串线（与 7.3.2 一致：只需要等待本地 stop 完成）
    session.cancelStreaming();
    session.clearConversations();

    const nextSessionId = uuidv4();
    const nextRequestSeq = ledgerRef.current[sessionKey].requestSeq + 1;
    ledgerRef.current[sessionKey] = {
      sessionId: nextSessionId,
      messageId: undefined,
      requestSeq: nextRequestSeq,
    };
    // 仅用于对外暴露/触发依赖刷新
    setSessionIdByKey((prev) => ({
      ...prev,
      [sessionKey]: nextSessionId,
    }));

    const input: IAiAnalysisDraftInternalInput = {
      ...buildAiAnalysisStreamingInput({
        ...runParams,
        session_id: nextSessionId,
      }),
      __sessionKey: sessionKey,
      __requestSeq: nextRequestSeq,
    };

    await session.sendMessage({ input });
  }, [getSessionByKey]);

  /**
   * 为下一次弹窗预览准备干净的 session。
   *
   * 关键规则：
   * - 如果当前已经有 attachedCardId，说明某条 session 正被卡片展示；
   *   这时预览必须切换到另一条 key，避免"弹窗预览"和"卡片展示"同时消费同一条流。
   * - 每次准备 preview 都会先 cancel + clear，确保报告页只展示本次参数对应的结果。
   */
  const preparePreviewSession = useCallback(() => {
    // 若已有"已 Apply 接管"的会话，则预览必须切到另一条 session key，避免同一会话被同时接管。
    const nextPreviewKey = attachedCardId ? getOppositeKey(attachedSessionKey) : previewSessionKey;
    setPreviewSessionKey(nextPreviewKey);
    cancelLatestByKey(nextPreviewKey);
  }, [
    attachedCardId,
    attachedSessionKey,
    cancelLatestByKey,
    previewSessionKey,
  ]);

  /**
   * 丢弃当前 preview 会话。
   *
   * 常见触发时机：
   * - 弹窗关闭且用户没有 Apply；
   * - 用户切换卡片重新打开弹窗，需要清掉上一次 preview 的残留。
   *
   * 这里要先判断"当前 preview 是否已经被接管"。
   * 一旦 attachedSessionKey === previewSessionKey，就说明这条流已经转交给卡片继续展示，
   * 此时再 discard 会把卡片上的内容一起清掉。
   */
  const discardPreviewSession = useCallback(() => {
    // 保险：如果 preview session 已经被 Apply 接管（等价于 attachedSessionKey === previewSessionKey），则不允许 discard。
    if (attachedCardId && attachedSessionKey === previewSessionKey) {
      return;
    }
    cancelLatestByKey(previewSessionKey);
  }, [
    attachedCardId,
    attachedSessionKey,
    cancelLatestByKey,
    previewSessionKey,
  ]);

  /**
   * 在 previewSession 上发起一次分析。
   * 发起前先把该 session 的 latestMessageId 重置，避免 stop/discard 误用上一轮请求的 messageId。
   */
  const runPreviewAnalysis = useCallback(
    async (runParams: IBuildAiAnalysisInputParams) => {
      await startAnalysisByKey(previewSessionKey, runParams);
    },
    [previewSessionKey, startAnalysisByKey],
  );

  const runAttachedAnalysisByCardId = useCallback(async (cardId: string, runParams: IBuildAiAnalysisInputParams) => {
    if (!attachedCardId || attachedCardId !== cardId) {
      return;
    }
    await startAnalysisByKey(attachedSessionKey, runParams);
  }, [attachedCardId, attachedSessionKey, startAnalysisByKey]);

  /**
   * 把"当前 preview 使用的那条 session"标记为某张卡片的 attachedSession。
   * 这里只改归属，不清空会话，也不重新请求后端。
   */
  const detachPreviewToCard = useCallback(
    (cardId: string) => {
      setAttachedCardId(cardId);
      setAttachedSessionKey(previewSessionKey);
    },
    [previewSessionKey],
  );

  /**
   * 删除卡片时，如果它正处于 attached 状态，就把对应会话也一起终止。
   * 否则后端 SSE 仍可能继续返回，造成无主请求占用资源。
   */
  const cleanupOnDeleteCard = useCallback(
    (cardId: string) => {
      if (!attachedCardId || attachedCardId !== cardId) {
        return;
      }
      cancelLatestByKey(attachedSessionKey);
      setAttachedCardId(undefined);
    },
    [attachedCardId, attachedSessionKey, cancelLatestByKey],
  );

  const releaseAttachedSessionByCardId = useCallback(
    (cardId: string) => {
      if (!attachedCardId || attachedCardId !== cardId) {
        return;
      }
      cancelLatestByKey(attachedSessionKey);
      setAttachedCardId(undefined);
    },
    [attachedCardId, attachedSessionKey, cancelLatestByKey],
  );

  // 判断某张卡片是否正接管着 preview 会话，供 controller/card 层做分支处理。
  const isCardAttached = useCallback((cardId: string): boolean => (
    Boolean(attachedCardId && attachedCardId === cardId)
  ), [attachedCardId]);

  /**
   * 显式停止 attached 会话。
   * 典型场景是卡片点击 Stop，而该卡当前展示的不是自己的独立 session，而是 preview 接管过来的那条流。
   */
  const stopAttachedSessionByCardId = useCallback((cardId: string) => {
    if (!isCardAttached(cardId)) {
      return;
    }
    cancelLatestByKey(attachedSessionKey);
  }, [attachedSessionKey, cancelLatestByKey, isCardAttached]);

  const setAiMetricsCallback = useCallback((cb: ((durationMs: number) => void) | null) => {
    aiMetricsCallbackRef.current = cb;
  }, []);

  return {
    attachedCardId,
    attachedSessionId: sessionIdByKey[attachedSessionKey],
    attachedSession: attachedSession as unknown as IUseStreamingConversationResult<IAiAnalysisStreamingInput>,
    previewSession: previewSession as unknown as IUseStreamingConversationResult<IAiAnalysisStreamingInput>,
    preparePreviewSession,
    runPreviewAnalysis,
    runAttachedAnalysisByCardId,
    detachPreviewToCard,
    discardPreviewSession,
    cleanupOnDeleteCard,
    releaseAttachedSessionByCardId,
    isCardAttached,
    stopAttachedSessionByCardId,
    setAiMetricsCallback,
  };
};

